import type {
	BaseItemSchema,
	Grading,
	OriginalItemTypes,
} from "@memorang/types";
import { WFD_SPELLING_VARIANTS } from "./dictation-spelling-variants";

export const isWriteFromDictation = (itemType?: OriginalItemTypes): boolean =>
	itemType === "WFDL" || itemType === "WFDL_CORE";

export const getAnswerKeyWords = (scoring?: Grading): string[] | undefined =>
	scoring && "type" in scoring && scoring.type === "ANSWER_KEY_TEXT"
		? scoring.answerKey.map((entry) => entry.word)
		: undefined;

export const getDictationAnswerKeyWords = (
	item: BaseItemSchema,
): string[] | undefined =>
	isWriteFromDictation(item.metadata?.type)
		? getAnswerKeyWords(item.scoring)
		: undefined;

const WFD_NORMALIZED_MAX = 5;

export const normalizeWord = (word: string) =>
	word.toLowerCase().replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, "");

export const countCorrectDictationWords = (
	answerKeyWords: string[],
	responseText: string,
	spellingVariants: Readonly<
		Record<string, readonly string[]>
	> = WFD_SPELLING_VARIANTS,
): number => {
	const availableCounts = new Map<string, number>();
	for (const token of responseText.split(/\s+/)) {
		const word = normalizeWord(token);
		if (word) {
			availableCounts.set(word, (availableCounts.get(word) ?? 0) + 1);
		}
	}
	const consume = (word: string): boolean => {
		const available = availableCounts.get(word) ?? 0;
		if (available === 0) {
			return false;
		}
		availableCounts.set(word, available - 1);
		return true;
	};
	return answerKeyWords.reduce((count, keyWord) => {
		const word = normalizeWord(keyWord);
		if (consume(word)) {
			return count + 1;
		}
		const variants = Object.hasOwn(spellingVariants, word)
			? (spellingVariants[word] ?? [])
			: [];
		for (const variant of variants) {
			if (consume(variant)) {
				return count + 1;
			}
		}
		return count;
	}, 0);
};

// Kept unrounded: per-skill GSE sums accumulate the fractions and round once at
// the end. `scoring.maxNormalizedScore` is deliberately ignored; it is null on
// every production WFD item and cannot be trusted.
export const toNormalizedDictationScore = (
	numCorrectWords: number,
	numAnswerKeyWords: number,
): number =>
	numAnswerKeyWords === 0
		? 0
		: (numCorrectWords / numAnswerKeyWords) * WFD_NORMALIZED_MAX;
